/**
 * ELT Job API Service
 *
 * API methods for ELT job operations including manual triggering.
 * Updated to use standardized API response format.
 */

import apiClient from "./client";
import type { ApiSuccessResponse, ApiCollectionResponse } from "../types/api.types";
import type { EltJob, EltJobCreateDTO, EltJobUpdateDTO, EltJobTriggerDTO, EltJobListQuery, EltJobExportResponse, EltJobImportResponse } from "../types/elt-job.types";
import type { EltJobExecution } from "../types/elt-job-execution.types";

// ============================================================================
// ELT JOB SERVICE
// ============================================================================

export const eltJobService = {
  /**
   * List all ELT jobs for a tenant
   */
  listEltJobs: async (tenantId: string, query?: EltJobListQuery): Promise<EltJob[]> => {
    const response = await apiClient.get<ApiCollectionResponse<EltJob>>(`/tenants/${tenantId}/elt-jobs`, { params: query });
    return response.data.data;
  },

  /**
   * Get ELT job by ID
   */
  getEltJob: async (tenantId: string, eltJobId: string): Promise<EltJob> => {
    const response = await apiClient.get<ApiSuccessResponse<EltJob>>(`/tenants/${tenantId}/elt-jobs/${eltJobId}`);
    return response.data.data;
  },

  /**
   * Create a new ELT job
   */
  createEltJob: async (tenantId: string, data: EltJobCreateDTO): Promise<EltJob> => {
    const response = await apiClient.post<ApiSuccessResponse<EltJob>>(`/tenants/${tenantId}/elt-jobs`, data);
    return response.data.data;
  },

  /**
   * Update ELT job
   */
  updateEltJob: async (tenantId: string, eltJobId: string, data: EltJobUpdateDTO): Promise<EltJob> => {
    const response = await apiClient.put<ApiSuccessResponse<EltJob>>(`/tenants/${tenantId}/elt-jobs/${eltJobId}`, data);
    return response.data.data;
  },

  /**
   * Delete ELT job
   */
  deleteEltJob: async (tenantId: string, eltJobId: string): Promise<void> => {
    await apiClient.delete(`/tenants/${tenantId}/elt-jobs/${eltJobId}`);
  },

  /**
   * Trigger ELT job manually
   * Returns the created job execution
   */
  triggerJob: async (tenantId: string, eltJobId: string, data?: EltJobTriggerDTO): Promise<EltJobExecution> => {
    const response = await apiClient.post<ApiSuccessResponse<EltJobExecution>>(`/tenants/${tenantId}/elt-jobs/${eltJobId}/trigger`, data || {});
    return response.data.data;
  },

  /**
   * Export ELT jobs
   */
  exportEltJobs: async (tenantId: string, eltJobIds?: string[]): Promise<EltJobExportResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<EltJobExportResponse>>(`/tenants/${tenantId}/elt-jobs/export`, { eltJobIds });
    return response.data.data;
  },

  /**
   * Import ELT jobs
   */
  importEltJobs: async (tenantId: string, jobs: EltJobCreateDTO[]): Promise<EltJobImportResponse> => {
    const response = await apiClient.post<ApiSuccessResponse<EltJobImportResponse>>(`/tenants/${tenantId}/elt-jobs/import`, { jobs });
    return response.data.data;
  },
};

export default eltJobService;
